/**
 * Utility functions and device detection
 */

/**
 * Device detection using matchMedia (reactive getters)
 * Values update automatically on viewport changes
 */
const _ua = {
  get TB() {
    return window.matchMedia('(max-width: 1023px) and (min-width: 768px), (max-device-width: 1366px) and (orientation: portrait)').matches;
  },
  get SP() {
    return window.matchMedia('(max-width: 767px)').matches;
  }
};

/**
 * Breakpoint detection using matchMedia (reactive getters)
 * Values update automatically on viewport changes
 */
const _breakP = {
  get PC() {
    return window.matchMedia('(min-width: 1024px)').matches;
  },
  get TB() {
    return window.matchMedia('(max-width: 1023px) and (min-width: 768px)').matches;
  },
  get SP() {
    return window.matchMedia('(max-width: 767px)').matches;
  }
};

/**
 * Development-only logging with styled output
 * @param {string} key - Label for the log
 * @param {string} value - Value to log
 * @param {string} color - Background color for the label
 */
const _log = (key, value, color = '#333') => {
  if (location.href.indexOf('localhost') > 0) {
    console.log(
      `%c${key}%c ${value}`,
      `padding: 2px 6px; background: ${color}; color: #fff; border-radius: 2px;`,
      ''
    );
  }
};

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
const debounce = (func, wait = 100) => {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
};

/**
 * Throttle function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit time in milliseconds
 * @returns {Function} Throttled function
 */
const throttle = (func, limit = 100) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
};

/**
 * モバイルデバイス判定（タッチ対応 + モバイル画面幅）
 * タッチ対応PCではfalseを返し、Lenis/GSAPを有効にする
 *
 * 注意: この関数は呼び出し時点の画面幅で判定します。
 * 各コントローラーでは初期化時に一度だけ呼び出し、その値を保持します。
 * （画面リサイズによるモバイル⇔PC切り替えには対応していません）
 *
 * ブレークポイント: max-width: 767px（$breakP: 768px未満がSP）
 * @returns {boolean} モバイルデバイスかどうか
 */
const isMobileDevice = () => {
  const hasTouchCapability = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  // $breakP: 768px に合わせて 767px 以下をモバイルと判定
  const isMobileWidth = window.matchMedia('(max-width: 767px)').matches;
  return hasTouchCapability && isMobileWidth;
};

/**
 * GSAP初期化管理（シングルトン）
 * 複数箇所からの重複初期化を防ぎ、一度だけregisterPluginを実行
 */
let gsapInstance = null;
let scrollTriggerInstance = null;
let gsapInitPromise = null;

const getGSAP = async () => {
  // 既に初期化済みの場合はキャッシュを返す
  if (gsapInstance && scrollTriggerInstance) {
    return { gsap: gsapInstance, ScrollTrigger: scrollTriggerInstance };
  }

  // 初期化中の場合は同じPromiseを返す（競合防止）
  if (gsapInitPromise) {
    return gsapInitPromise;
  }

  gsapInitPromise = (async () => {
    try {
      const [{ default: gsap }, { ScrollTrigger }] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger')
      ]);

      gsap.registerPlugin(ScrollTrigger);

      // モバイルでのリサイズ時の再計算を抑制
      ScrollTrigger.config({ ignoreMobileResize: true });

      gsapInstance = gsap;
      scrollTriggerInstance = ScrollTrigger;

      return { gsap, ScrollTrigger };
    } catch (error) {
      // エラー時はPromiseをリセットして再試行可能にする
      gsapInitPromise = null;
      console.error('[getGSAP] Failed to initialize GSAP:', error);
      throw error;
    }
  })();

  return gsapInitPromise;
};

export { _ua, _breakP, _log, debounce, throttle, isMobileDevice, getGSAP };
